import React, { useRef, useMemo, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Environment } from "@react-three/drei";
import * as THREE from "three";

/*
 * Lightweight, procedurally-built 3D "Repair Lab" scene.
 * - Floating smartphone (rounded rect with a screen)
 * - Floating laptop (base + lid)
 * - Motherboard-like chip with pins & rings (CPU)
 * - Small microchips and glowing spheres
 * - Circuit particles
 * No external GLTF assets; ships fast, works offline.
 */

function Phone({ position = [0, 0, 0], color = "#0e1b26" }) {
  return (
    <group position={position} rotation={[0.1, 0.4, 0.1]}>
      <mesh castShadow receiveShadow>
        <boxGeometry args={[1.1, 2.2, 0.12]} />
        <meshStandardMaterial color={color} metalness={0.85} roughness={0.25} />
      </mesh>
      <mesh position={[0, 0, 0.062]}>
        <boxGeometry args={[0.95, 2.0, 0.02]} />
        <meshStandardMaterial
          color="#001a1f"
          emissive="#00e5ff"
          emissiveIntensity={0.35}
          metalness={0.4}
          roughness={0.15}
        />
      </mesh>
      {/* Notch */}
      <mesh position={[0, 0.9, 0.075]}>
        <boxGeometry args={[0.3, 0.05, 0.02]} />
        <meshStandardMaterial color="#000" />
      </mesh>
    </group>
  );
}

function Laptop({ position = [0, 0, 0] }) {
  return (
    <group position={position} rotation={[0.35, -0.35, 0]}>
      {/* Base */}
      <mesh>
        <boxGeometry args={[2.4, 0.1, 1.6]} />
        <meshStandardMaterial color="#0f1720" metalness={0.9} roughness={0.3} />
      </mesh>
      {/* Keyboard glow */}
      <mesh position={[0, 0.055, 0]}>
        <boxGeometry args={[2.15, 0.005, 1.35]} />
        <meshStandardMaterial
          color="#001b23"
          emissive="#22e6a3"
          emissiveIntensity={0.4}
        />
      </mesh>
      {/* Lid */}
      <group position={[0, 0.9, -0.78]} rotation={[-0.55, 0, 0]}>
        <mesh>
          <boxGeometry args={[2.4, 1.55, 0.08]} />
          <meshStandardMaterial color="#0e1720" metalness={0.9} roughness={0.28} />
        </mesh>
        <mesh position={[0, 0, 0.045]}>
          <boxGeometry args={[2.2, 1.35, 0.01]} />
          <meshStandardMaterial
            color="#00131a"
            emissive="#00e5ff"
            emissiveIntensity={0.55}
          />
        </mesh>
      </group>
    </group>
  );
}

function CPU({ position = [0, 0, 0] }) {
  return (
    <group position={position} rotation={[0.3, 0.6, 0.1]}>
      <mesh>
        <boxGeometry args={[1.4, 0.16, 1.4]} />
        <meshStandardMaterial color="#0a1218" metalness={0.9} roughness={0.35} />
      </mesh>
      <mesh position={[0, 0.09, 0]}>
        <boxGeometry args={[1.0, 0.05, 1.0]} />
        <meshStandardMaterial
          color="#04252b"
          emissive="#00e5ff"
          emissiveIntensity={0.7}
          metalness={0.6}
          roughness={0.2}
        />
      </mesh>
      {/* Pins */}
      {Array.from({ length: 8 }).map((_, i) => (
        <mesh key={i} position={[-0.7 + i * 0.2, -0.09, 0.7]}>
          <boxGeometry args={[0.06, 0.05, 0.15]} />
          <meshStandardMaterial color="#aa8b3a" metalness={1} roughness={0.3} />
        </mesh>
      ))}
    </group>
  );
}

function Chip({ position = [0, 0, 0], color = "#22e6a3" }) {
  return (
    <group position={position} rotation={[0.2, 0.4, 0]}>
      <mesh>
        <boxGeometry args={[0.7, 0.1, 0.7]} />
        <meshStandardMaterial color="#0d1720" metalness={0.9} roughness={0.35} />
      </mesh>
      <mesh position={[0, 0.06, 0]}>
        <boxGeometry args={[0.55, 0.03, 0.55]} />
        <meshStandardMaterial color="#031218" emissive={color} emissiveIntensity={0.9} />
      </mesh>
    </group>
  );
}

function Particles({ count = 90 }) {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3 + 0] = (Math.random() - 0.5) * 14;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 8;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 6;
    }
    return arr;
  }, [count]);

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y = state.clock.elapsedTime * 0.03;
    }
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        color="#00e5ff"
        transparent
        opacity={0.85}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}

function SceneContents() {
  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} color="#8fdcff" />
      <pointLight position={[-4, -2, 2]} intensity={1.5} color="#22e6a3" distance={12} />
      <pointLight position={[4, 3, -2]} intensity={1.5} color="#2f80ff" distance={12} />

      <Float speed={1.4} rotationIntensity={0.6} floatIntensity={1.2}>
        <Phone position={[-2.6, 0.6, 0]} />
      </Float>
      <Float speed={1.1} rotationIntensity={0.5} floatIntensity={1.0}>
        <Laptop position={[1.6, -0.4, -0.5]} />
      </Float>
      <Float speed={1.6} rotationIntensity={0.8} floatIntensity={1.4}>
        <CPU position={[-1.2, -1.4, 0.8]} />
      </Float>
      <Float speed={1.8} rotationIntensity={0.9} floatIntensity={1.6}>
        <Chip position={[2.9, 1.4, 0.4]} color="#22e6a3" />
      </Float>
      <Float speed={2.0} rotationIntensity={0.9} floatIntensity={1.4}>
        <Chip position={[-3.4, -0.6, 1.1]} color="#00e5ff" />
      </Float>
      <Float speed={1.3} rotationIntensity={0.6} floatIntensity={1.2}>
        <Chip position={[0.4, 1.9, -1.1]} color="#2f80ff" />
      </Float>

      <Particles count={110} />
      <Suspense fallback={null}>
        <Environment preset="night" />
      </Suspense>
    </>
  );
}

export default function LabScene({ className = "" }) {
  return (
    <div className={className} data-testid="lab-3d-scene">
      <Canvas
        dpr={[1, 1.6]}
        camera={{ position: [0, 0.4, 7], fov: 45 }}
        gl={{ antialias: true, alpha: true }}
      >
        <SceneContents />
      </Canvas>
    </div>
  );
}
